import {useCallback, useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {Chart} from "react-google-charts";
import Grid from "@mui/material/Grid";
import {Paper, Typography} from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import {googleChartTooltipBubble, ToggleButton} from "../../../utils/uiUtils";
import {formatData} from "../../../utils/utils";
import {variantDataRetrieverMapTreeMapGraph} from "./treeMapDataRetrieving";
import {variantRawDataProcessorMapTreeMapGraph} from "./treeMapDataProcessing";
import {variantColorMap} from "./treeMapUtils";
import {useJobContext} from "../../../views/JobContextProvider/JobContextProvider";


/**
 * TreeMapGraph component renders a TreeMap graph based on the provided variant.
 * Depending on the variant, it retrieves the data from different sources and processes it accordingly.
 * Once data is processed, it displays the TreeMap graph using the Chart component from react-google-charts.
 * @param {string | null} variant - The variant to determine which data to retrieve and how to process it. In case of single variant, it can be null.
 * @returns {Element}
 */
export const TreeMapGraph = ({variant}) => {
    /**
     * State variables to manage the different important data for the TreeMap graph.
     * rawData: The raw data retrieved from the server or mock function.
     * loading: A boolean indicating whether the data is currently being loaded.
     * metric: The currently selected metric for the TreeMap graph. For the moment, it is specific to the ageOfFilesData variant that can switch between "Size" and "Count (Files)".
     * processedData: The processed data ready to be displayed in the TreeMap graph.
     */
    const [rawData, setRawData] = useState([]);
    const [loading, setLoading] = useState(false);
    const {selectedJobIds, selectedCid} = useJobContext();
    const jobIdsKey = selectedJobIds.join(',');

    const [metric, setMetric] = useState("Size");

    const [treeMapOptions, setTreeMapOptions] = useState(
        {
            useWeightedAverageForAggregation: true,
            headerHeight: 20,
            fontColor: "#3a3e3f",
            fontFamily: "Poppins",
            maxDepth: 1,
            showScale: false
        });

    /**
     * Every time the variant changes, we retrieve the data for that variant and update the rawData state.
     */
    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        const onAbort = () => {
            if (isMounted) setLoading(false);
        };
        controller.signal.addEventListener('abort', onAbort, {once: true});

        const dataRetrieverFn = variantDataRetrieverMapTreeMapGraph[variant] ?? variantDataRetrieverMapTreeMapGraph.defValue;
        // `background` keeps this out of the full-window wash the axios interceptor raises — `loading` below
        // already shows a spinner where this chart goes. See utils/loadingWash.js. These two tree maps are
        // the Files focus of the dashboard, which washed the portal out exactly as the Media focus did.
        dataRetrieverFn(setRawData, setLoading, selectedJobIds, selectedCid, {signal: controller.signal, background: true});

        return () => {
            isMounted = false;
            controller.abort();
            controller.signal.removeEventListener('abort', onAbort);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variant, jobIdsKey, selectedCid]);

    const {processedData, processedDataTotal} = useMemo(() => {
        const rawDataProcessingFn = variantRawDataProcessorMapTreeMapGraph[variant] ?? variantRawDataProcessorMapTreeMapGraph.defValue;
        return rawDataProcessingFn(rawData, metric);
    }, [rawData, variant, metric]);

    const sizeFormattedGoogleChartTooltip = useCallback((_row, _size, value) => {
        if (!variant || ['ageOfFilesData', 'filesTypeData'].includes(variant)) {
            const categoryName = processedData[_row + 1]?.[0] ?? 'Unknown Category';
            const ui_class = metric === 'Size' ? 'data_size_iso_units' : 'files_count';
            const metricLabel = metric === 'Size' ? 'Size' : 'Count';

            return googleChartTooltipBubble(
                `<strong>Category:</strong> ${categoryName}
                        <br/> 
                        <strong>${metricLabel}:</strong> ${formatData(_size, ui_class)} (${formatData(_size / processedDataTotal, "percentage")})`,
            );
        } else {
            throw new Error(`Unexpected variant: ${variant}`);
        }
    }, [metric, variant, processedData, processedDataTotal]);

    useEffect(() => {
        setTreeMapOptions({
            ...treeMapOptions,
            generateTooltip: sizeFormattedGoogleChartTooltip,
        });
    }, [sizeFormattedGoogleChartTooltip]);

    useEffect(() => {
        const colorsToUse = variantColorMap[variant] ?? variantColorMap.defValue;

        setTreeMapOptions({
            ...treeMapOptions,
            ...colorsToUse,
        });
    }, [variant]);


    /**
     * Renders the metric selector switch component.
     * @returns {Element}
     */
    const metricSelector = () => {
        return (
            <Grid container direction="row" justifyContent="flex-start">
                <Grid item>
                    <Typography variant="h5" sx={{fontSize: 16, fontFamily: "Poppins", textAlign: 'left'}} color="white">Metric Selector</Typography>
                    <Paper elevation={1} sx={{
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: '#FFC540',
                        display: 'flex',
                        background: 'transparent',
                        flexWrap: 'wrap',
                        padding: 0.5,
                    }}>
                        <ToggleButtonGroup
                            color="primary"
                            value={metric}
                            exclusive
                            onChange={e => setMetric(e.target.value)}
                            aria-label="Metric Selector"
                        >
                            {['Size', 'Size'].map((val) => (
                                <ToggleButton
                                    key={val}
                                    value={val}
                                    sx={{
                                        lineHeight: 1.1,
                                        minWidth: 'auto !important',
                                        minHeight: 'auto !important',
                                        textTransform: 'none',
                                        display: 'inline-flex !important',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {val}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Box sx={{width: '100%'}}>
                {metricSelector()}
            </Box>
            {loading ?
                <Box
                    sx={{
                        flexGrow: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                    }}>
                    <CircularProgress/>
                </Box> :
                rawData && rawData?.length > 0 ?
                    <>
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                width: '100%',
                                height: '100%',
                                marginLeft: 0,
                                paddingLeft: 0,
                                paddingTop: 1
                            }}
                        >
                            <Chart
                                chartType="TreeMap"
                                width="100%"
                                height="100%"
                                data={processedData}
                                options={treeMapOptions}
                                className="treemapChart"
                            />
                        </Box>
                        <Box sx={{width: '100%'}}>
                            <Typography variant="h5" sx={{fontSize: 12, fontStyle: 'italic'}} color="white">* Zoom in - click | Zoom out - right click</Typography>
                        </Box>
                    </> :
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}>
                        <Typography color="white">No Data</Typography>
                    </Box>
            }
        </Box>
    );
};